import React, { useContext, useRef, useEffect, useState } from 'react';
import './AudioPlayer.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStepBackward,
  faBackward,
  faPlay,
  faPause,
  faForward,
  faStepForward,
} from '@fortawesome/free-solid-svg-icons';
import { AudioContext } from '../contexts/AudioContext';
import db, { formatChapterId, ProgressStatus, getCachedAudio, cacheAudio } from '../db';
import { loadPlan, nextUnreadInPlan, prevInPlan, toPlayable, sectionForChapter } from '../utils/plan';

function AudioPlayer() {
  const {
    currentChapter,
    setCurrentChapter,
    isPlaying,
    setIsPlaying,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    updateSession,
    chapterProgress,
    setChapterProgress,
  } = useContext(AudioContext);
  const audioRef = useRef(null);
  const [seekValue, setSeekValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioSource, setAudioSource] = useState(null);
  const [isAutoAdvanced, setIsAutoAdvanced] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(() => {
    try {
      const saved = parseFloat(localStorage.getItem('playbackSpeed'));
      if ([0.5, 0.75, 1, 1.25, 1.5, 2, 3].includes(saved)) return saved;
    } catch (e) { /* ignore */ }
    return 1;
  });
  const [showSpeedPopup, setShowSpeedPopup] = useState(false);

  const snapshotProgress = async () => {
    const rows = await db.chapterProgress.toArray();
    const progress = {};
    for (const p of rows) {
      progress[p.chapter_id] = { status: p.status, progress_percent: p.progress_percent };
    }
    if (currentChapter) {
      const id = formatChapterId(currentChapter.bookId, currentChapter.chapter);
      progress[id] = { status: ProgressStatus.COMPLETED, progress_percent: 100 };
    }
    return progress;
  };

  const playPreviousChapter = async () => {
    if (!currentChapter) return;
    setIsAutoAdvanced(false);
    if (currentChapter.fromPlan || currentChapter.planSection) {
      const plan = await loadPlan();
      const section = sectionForChapter(
        plan,
        currentChapter.bookId,
        currentChapter.chapter,
        currentChapter.planSection
      );
      const prev = section && prevInPlan(section, currentChapter.bookId, currentChapter.chapter);
      if (!prev) return;
      const playable = toPlayable(prev, section.id);
      setCurrentChapter(playable);
      updateSession(playable);
      return;
    }
    if (currentChapter.chapter > 1) {
      const book = await db.books.where({ name: currentChapter.book }).first();
      const prevChapter = {
        bookId: book.id,
        book: currentChapter.book,
        chapter: currentChapter.chapter - 1,
      };
      const url = getAudioUrl(prevChapter.bookId, prevChapter.book, prevChapter.chapter);
      setCurrentChapter({ ...prevChapter, url });
      updateSession(prevChapter);
    }
  };

  const playNextChapter = async () => {
    if (!currentChapter) return;

    if (currentChapter.fromPlan || currentChapter.planSection) {
      const plan = await loadPlan();
      const section = sectionForChapter(
        plan,
        currentChapter.bookId,
        currentChapter.chapter,
        currentChapter.planSection
      );
      const next = section && nextUnreadInPlan(
        section,
        currentChapter.bookId,
        currentChapter.chapter,
        await snapshotProgress()
      );
      if (!next) {
        setIsPlaying(false);
        setIsAutoAdvanced(false);
        return;
      }
      const playable = toPlayable(next, section.id);
      setCurrentChapter(playable);
      updateSession(playable);
      return;
    }
    
    const book = await db.books.where({ name: currentChapter.book }).first();
    if (currentChapter.chapter < book.chapters) {
      const nextChapter = {
        bookId: book.id,
        book: currentChapter.book,
        chapter: currentChapter.chapter + 1,
      };
      const url = getAudioUrl(nextChapter.bookId, nextChapter.book, nextChapter.chapter);
      setCurrentChapter({ ...nextChapter, url });
      updateSession(nextChapter);
    } else {
      const nextBook = await db.books.where('id').above(book.id).first();
      if (nextBook) {
        const nextChapter = {
          bookId: nextBook.id,
          book: nextBook.name,
          chapter: 1,
        };
        const url = getAudioUrl(nextChapter.bookId, nextChapter.book, nextChapter.chapter);
        setCurrentChapter({ ...nextChapter, url });
        updateSession(nextChapter);
      } else {
        setIsPlaying(false);
        setIsAutoAdvanced(false);
      }
    }
  };

  // Reset state when chapter changes
  useEffect(() => {
    if (!currentChapter) {
      setSeekValue(0);
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
      setAudioSource(null);
    } else if (currentChapter.url) {
      loadAudioFromCache(currentChapter.url);
    }
  }, [currentChapter]);

  // Fetch and cache audio
  const loadAudioFromCache = async (url) => {
    if (!url) return;
    
    setIsLoading(true);
    
    try {
      // Try to get from cache first
      let audioBlob = await getCachedAudio(url);
      
      if (!audioBlob) {
        // Not in cache, fetch and store
        audioBlob = await fetchAndCacheAudio(url);
      }
      
      if (audioBlob) {
        const objectUrl = URL.createObjectURL(audioBlob);
        setAudioSource(objectUrl);
        
        if (currentChapter) {
          preCacheNextChapter();
        }
      } else {
        console.error("Failed to load audio");
      }
    } catch (error) {
      console.error("Error loading audio:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchAndCacheAudio = async (url) => {
    try {
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const blob = await response.blob();
      
      // Cache the audio blob
      await cacheAudio(url, blob);
      
      return blob;
    } catch (error) {
      console.error("Error fetching audio:", error);
      return null;
    }
  };
  
  const preCacheNextChapter = async () => {
    if (!currentChapter) return;
    
    try {
      let nextChapterUrl = null;
      if (currentChapter.fromPlan || currentChapter.planSection) {
        const plan = await loadPlan();
        const section = sectionForChapter(
          plan,
          currentChapter.bookId,
          currentChapter.chapter,
          currentChapter.planSection
        );
        const next = section && nextUnreadInPlan(
          section,
          currentChapter.bookId,
          currentChapter.chapter,
          await snapshotProgress()
        );
        if (next) nextChapterUrl = getAudioUrl(next.bookId, next.book, next.chapter);
      } else {
        const book = await db.books.where({ name: currentChapter.book }).first();
        if (currentChapter.chapter < book.chapters) {
          nextChapterUrl = getAudioUrl(book.id, currentChapter.book, currentChapter.chapter + 1);
        } else {
          const nextBook = await db.books.where('id').above(book.id).first();
          if (nextBook) {
            nextChapterUrl = getAudioUrl(nextBook.id, nextBook.name, 1);
          }
        }
      }
      
      if (nextChapterUrl) {
        const cached = await getCachedAudio(nextChapterUrl);
        if (!cached) {
          fetchAndCacheAudio(nextChapterUrl);
        }
      }
    } catch (error) {
      console.error("Error pre-caching next chapter:", error);
    }
  };

  // Effect to control playback (play/pause)
  useEffect(() => {
    if (currentChapter && audioRef.current && !isLoading) {
      if (isPlaying) {
        audioRef.current.play().catch((e) => {
          console.error('Playback error:', e);
          setIsPlaying(false);  // Reset play state on error
        });
      } else {
        audioRef.current.pause();
      }
    } else if (isLoading && isPlaying) {
      // If audio is still loading but play was requested, wait for loading to complete
      setIsPlaying(true);
    }
  }, [isPlaying, currentChapter, isLoading, audioSource]);

  // Effect to handle audio source changes
  useEffect(() => {
    if (audioSource && audioRef.current) {
      // When audio source changes, if we were playing, resume playback
      if (isPlaying && !isLoading) {
        audioRef.current.play().catch(e => {
          console.error('Error playing after source change:', e);
          setIsPlaying(false);
        });
      }

    }
  }, [audioSource, isPlaying, isLoading]);

  const updateChapterProgress = async (chapterId, progressPercent) => {
    try {
      // Get existing progress to check if it was already completed
      const existingProgress = await db.chapterProgress.get(chapterId);
      const wasCompleted = existingProgress?.status === ProgressStatus.COMPLETED;
      
      // Determine the new status
      let status;
      if (wasCompleted) {
        // Keep the completed state if it was already completed
        status = ProgressStatus.COMPLETED;
      } else {
        // Otherwise, use the normal status determination logic
        status = progressPercent >= 99.5 ? 
          ProgressStatus.COMPLETED : 
          progressPercent > 0.5 ? 
            ProgressStatus.IN_PROGRESS : 
            ProgressStatus.NOT_STARTED;
      }

      // Only update if progress is past 0.5% or completing the chapter
      if (progressPercent > 0.5 || progressPercent >= 99.5) {
        const newProgress = {
          chapter_id: chapterId,
          status,
          progress_percent: progressPercent,
          last_updated_at: new Date().toISOString(),
          dirty: 1
        };

        await db.chapterProgress.put(newProgress);
        
        // Update the context state to reflect changes immediately
        setChapterProgress(prev => ({
          ...prev,
          [chapterId]: { status, progress_percent: progressPercent }
        }));
      }
    } catch (error) {
      console.error('Error updating chapter progress:', error);
    }
  };

  const handleEnded = async () => {
    if (!currentChapter || isDragging) return;
    
    const chapterId = formatChapterId(currentChapter.bookId, currentChapter.chapter);
    await updateChapterProgress(chapterId, 100);
    
    // Set the auto-advanced flag before playing next chapter
    setIsAutoAdvanced(true);
    
    // Reset position state if available
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setPositionState({
        duration: 0,
        playbackRate: playbackSpeed,
        position: 0
      });
    }
    
    await playNextChapter();
  };

  // Effect to handle audio events (timeupdate, loadedmetadata, ended)
  useEffect(() => {
    if (!currentChapter || !audioRef.current) return;

    const audio = audioRef.current;
    const chapterId = formatChapterId(currentChapter.bookId, currentChapter.chapter);

    // Try to resume from last position, but only if chapter has meaningful progress
    // and was not auto-advanced from previous chapter
    const loadProgress = async () => {
      try {
        if (isAutoAdvanced) {
          // If chapter was auto-advanced (i.e., previous chapter ended), start from the beginning
          audio.currentTime = 0;
          setSeekValue(0);
          // Reset the flag after using it
          setIsAutoAdvanced(false);
        } else {
          const progress = await db.chapterProgress.get(chapterId);
          if (progress && 
              progress.progress_percent > 0.5 && 
              progress.status !== ProgressStatus.NOT_STARTED && 
              progress.progress_percent < 99.5) {  // Don't resume if it's completed (99.5%+)
            const timeToSeek = (progress.progress_percent / 100) * audio.duration;
            audio.currentTime = timeToSeek;
            setSeekValue(progress.progress_percent);
          } else {
            audio.currentTime = 0;
            setSeekValue(0);
          }
        }
      } catch (error) {
        console.error('Error loading progress:', error);
        audio.currentTime = 0;
        setSeekValue(0);
      }
    };

    const updateTime = () => {
      if (!audio.duration) return;
      const currentTime = audio.currentTime;
      setCurrentTime(currentTime);
      
      if (!isDragging) {
        const progressPercent = (currentTime / audio.duration) * 100;
        setSeekValue(progressPercent);
          
        // Update chapter progress
        if (progressPercent !== chapterProgress[chapterId]?.progress_percent) {
          updateChapterProgress(chapterId, progressPercent);
        }
      }
    };

    const handleMetadata = () => {
      setDuration(audio.duration);
      loadProgress();
    };

    const handleError = (error) => {
      console.error('Audio loading error:', error);
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleMetadata);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    if (audio.duration) handleMetadata();

    return () => {
      audio.removeEventListener('loadedmetadata', handleMetadata);
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [currentChapter, isDragging, isAutoAdvanced, audioSource]);

  // Add this effect to implement MediaSession API
  useEffect(() => {
    if (!('mediaSession' in navigator) || !currentChapter) return;
    
    // Set metadata for media session
    navigator.mediaSession.metadata = new MediaMetadata({
      title: `Chapter ${currentChapter.chapter}`,
      artist: currentChapter.book,
      album: 'ESV Bible',
      // Add artwork if available later
    });

    // Handle skip to next track - critical for screen-off functionality
    navigator.mediaSession.setActionHandler('nexttrack', () => {
      handleManualNextChapter();
    });

    // Handle skip to previous track
    navigator.mediaSession.setActionHandler('previoustrack', () => {
      playPreviousChapter();
    });
    
    // This is the most important part - set an action handler for when track ends
    navigator.mediaSession.setActionHandler('stop', async () => {
      // Save our progress first
      if (currentChapter) {
        const chapterId = formatChapterId(currentChapter.bookId, currentChapter.chapter);
        await updateChapterProgress(chapterId, 100);
      }
      setIsAutoAdvanced(true);
      await playNextChapter();
    });

    navigator.mediaSession.setActionHandler('play', () => {
      setIsPlaying(true);
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      setIsPlaying(false);
    });

    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime && audioRef.current) {
        audioRef.current.currentTime = details.seekTime;
        
        // If seeking to end of track, update progress and prepare for next chapter
        if (details.seekTime >= audioRef.current.duration - 0.5) {
          const chapterId = formatChapterId(currentChapter.bookId, currentChapter.chapter);
          updateChapterProgress(chapterId, 100);
          setIsAutoAdvanced(true);
        }
      }
    });

    return () => {
      // Clean up action handlers when component unmounts
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('stop', null);
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('seekto', null);
      }
    };
  }, [currentChapter, playbackSpeed]);

  // Reset auto-advanced flag when user manually navigates
  const handleManualNextChapter = async () => {
    setIsAutoAdvanced(false);
    await playNextChapter();
  };

  const skip = (seconds) => {
    if (!audioRef.current || !currentChapter || isLoading) return;
    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    handleSeekToTime(newTime);
  };

  const getAudioUrl = (bookId, bookName, chapter) => {
    const paddedBookId = bookId.toString().padStart(2, '0');
    const paddedChapter = chapter.toString().padStart(3, '0');
    const unpaddedChapter = chapter.toString();
    return `/static/audio/esv/${paddedBookId}${paddedChapter}_${bookName} ${unpaddedChapter}.mp3`;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeekChange = (e) => {
    if (!audioRef.current || !currentChapter || !duration || isLoading) return;
    const newTime = (e.target.value / 100) * duration;
    
    // Keep it slightly before the end if we're still dragging
    const adjustedTime = isDragging && newTime >= duration ? duration - 0.01 : newTime;
    
    // Use our enhanced seek method
    handleSeekToTime(adjustedTime);
    
    setCurrentTime(adjustedTime);
    setSeekValue(e.target.value);
    
    // Resume playback if we were playing when seeking started
    if (isPlaying) {
      audioRef.current.play();
    }
  };

  const handleSeekMouseDown = () => {
    if (!currentChapter || isLoading) return;
    setIsDragging(true);
  };

  const handleSeekMouseUp = async () => {
    if (!currentChapter || isLoading) return;
    setIsDragging(false);

    // If we were playing and we're at the end, move to next chapter
    if (isPlaying && Math.abs(audioRef.current.currentTime - duration) < 0.1) {
      const chapterId = formatChapterId(currentChapter.bookId, currentChapter.chapter);
      await updateChapterProgress(chapterId, 100);
      setIsAutoAdvanced(true); // Set the flag before playing next chapter
      await playNextChapter();
    } else if (isPlaying) {
      // Otherwise just resume playback
      audioRef.current.play();
    }
  };
  
  // Helper for seeking to a specific time
  const handleSeekToTime = (time) => {
    if (!audioRef.current || !currentChapter || isLoading) return;
    audioRef.current.currentTime = time;
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    try {
      localStorage.setItem('playbackSpeed', String(speed));
    } catch (e) { /* ignore */ }
    setShowSpeedPopup(false);
  };

  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

  // Update playback speed when it changes or when audio loads
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed, audioSource]);

  // Clean up object URLs on component unmount
  useEffect(() => {
    return () => {
      if (audioSource) {
        URL.revokeObjectURL(audioSource);
      }
    };
  }, [audioSource]);

  // Bridge for Flutter WebView
  useEffect(() => {
    // Only run this if we are inside the Flutter WebView
    if (!window.flutter_inappwebview) return;

    // 1. Expose controls for Flutter to call
    window.flutterAudioControl = {
      play: () => setIsPlaying(true),
      pause: () => setIsPlaying(false),
      next: () => handleManualNextChapter(),
      prev: () => playPreviousChapter(),
      seek: (seconds) => skip(seconds), // For +10/-10 buttons if needed
    };

    // 2. Send state updates to Flutter
    if (currentChapter) {
      window.flutter_inappwebview.callHandler('updateAudioState', {
        title: `Chapter ${currentChapter.chapter}`,
        artist: currentChapter.book,
        album: 'ESV Bible',
        playing: isPlaying,
        duration: Math.floor(duration * 1000), // ms
        position: Math.floor(currentTime * 1000), // ms
      });
    }
  }, [currentChapter, isPlaying, currentTime, duration]);

  return (
    <div className="audio-player">
      <div className="seek-bar-container">
        <div className="seek-bar-background" />
        <div 
          className={`seek-bar-progress ${!currentChapter || isLoading? 'disabled' : ''}`}
          style={{ width: `calc(${seekValue}% + var(--thumb-size) / 2 - ${seekValue} * var(--thumb-size) / 100)` }} 
        />
        <input
          type="range"
          min="0"
          max="100"
          step = "0.01"
          value={seekValue}
          disabled={!currentChapter || isLoading}
          onChange={handleSeekChange}
          onMouseDown={handleSeekMouseDown}
          onMouseUp={handleSeekMouseUp}
          onTouchStart={handleSeekMouseDown}
          onTouchEnd={handleSeekMouseUp}
          className="seek-bar"
        />
      </div>
      <div className={`time-indicator ${isLoading ? 'loading' : ''}`}>
        {currentChapter ? (
          isLoading ? "Loading audio..." : `${formatTime(currentTime)} / ${formatTime(duration)}`
        ) : (
          "0:00 / 0:00"
        )}
      </div>
      <div className="controls">
        <div className="controls-center">
          <button onClick={playPreviousChapter} disabled={!currentChapter || isLoading}>
            <FontAwesomeIcon icon={faStepBackward} />
          </button>
          <button onClick={() => skip(-10)} disabled={!currentChapter || isLoading}>
            <FontAwesomeIcon icon={faBackward} />
          </button>
          <button onClick={() => setIsPlaying(!isPlaying)} disabled={!currentChapter || (isLoading && !audioSource)}>
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>
          <button onClick={() => skip(10)} disabled={!currentChapter || isLoading}>
            <FontAwesomeIcon icon={faForward} />
          </button>
          <button onClick={handleManualNextChapter} disabled={!currentChapter || isLoading}>
            <FontAwesomeIcon icon={faStepForward} />
          </button>
        </div>
        <div className="controls-right">
          <button 
            className="speed-button" 
            onClick={() => setShowSpeedPopup(!showSpeedPopup)}
            disabled={!currentChapter || isLoading}
          >
            {playbackSpeed}x
          </button>
          {showSpeedPopup && (
            <div className="speed-popup">
              {speedOptions.map((speed) => (
                <button
                  key={speed}
                  className={`speed-option ${speed === playbackSpeed ? 'active' : ''}`}
                  onClick={() => handleSpeedChange(speed)}
                >
                  {speed}x
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {audioSource && <audio ref={audioRef} src={audioSource} />}
    </div>
  );
}

export default AudioPlayer;